import { Link } from "react-router-dom";
import type { Article } from "@/lib/types";
import { formatCount, formatDate } from "@/lib/format";

type ArticleCardProps = {
  article: Article;
  variant?: "default" | "compact" | "wide";
};

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <article className="group flex gap-3">
        <Link
          to={`/article/${article.id}`}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-background-200"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - ${article.categoryName}`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-col justify-between py-0.5">
          <Link
            to={`/article/${article.id}`}
            className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors hover:text-primary-600"
          >
            {article.title}
          </Link>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">{article.categoryName}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(article.views)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "wide") {
    return (
      <article className="group grid grid-cols-1 gap-5 border-b border-background-200 pb-6 sm:grid-cols-[280px_1fr]">
        <Link
          to={`/article/${article.id}`}
          className="relative h-44 w-full overflow-hidden rounded-lg bg-background-200 sm:h-40"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - ${article.categoryName}`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-xs">
            <Link
              to={`/category/${article.categorySlug}`}
              className="rounded-full bg-primary-100 px-2.5 py-0.5 font-medium text-primary-700"
            >
              {article.categoryName}
            </Link>
            {article.hot && (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-900">
                <i className="ri-fire-fill"></i>
                热议
              </span>
            )}
          </div>
          <Link to={`/article/${article.id}`} className="mt-3">
            <h3 className="text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-xl">
              {article.title}
            </h3>
          </Link>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {formatCount(article.comments)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200">
      <Link to={`/article/${article.id}`} className="relative block h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - ${article.categoryName}`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-0.5 text-xs font-medium text-primary-700 backdrop-blur">
          {article.categoryName}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <Link to={`/article/${article.id}`}>
          <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
        </Link>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {formatCount(article.comments)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}